import { css, ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import FormError from '~/components/FormError/FormError';
import Label, { LabelPosition, LabelProps } from '~/components/Label/Label';
import { FlexContainer } from '~/components/Layout';
import { GreyLink } from '~/components/Link';
import Popup from '~/components/Popup';
import { HeightSize, InputSize } from '~/constants/form';
import * as React from 'react';
import { WrappedFieldProps } from 'redux-form';
import { colors, fontFamily, fontWeight, formSizes } from '~/styles/constants';

const LabelWithUrl = styled('div')`
  display: flex;
  justify-content: space-between;
`;

interface Theme {
  height: string;
  width: number;
  responsive: boolean;
  borderColor: string;
  backgroundColor: string;
}

const FormFieldFocusedMessage = styled('div')`
  color: ${colors.gray700};
  font-weight: ${fontWeight.normal};
  font-size: 12px;
  line-height: 18px;
  margin-top: 8px;
`;

interface MaskedTextProps {
  disabled?: boolean;
  onClick?: () => void;
  tabIndex?: number;
}

// eslint-disable-next-line @typescript-eslint/ban-types
const StyledMaskedText = styled('div')<MaskedTextProps>`
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  color: ${(props) =>
    props.disabled ? colors.form.disabledLabelFontColor : colors.form.fontColor};
`;

export const StyledInput = styled.input<{ theme?: Theme }>`
  height: ${(props) => (props.theme.height ? `${props.theme.height}px` : formSizes.inputHeight)};
  line-height: ${(props) =>
    props.theme.height ? `${props.theme.height}px` : formSizes.inputHeight};
  border-radius: 3px;
  border: 1px solid ${(props) => props.theme.borderColor};
  font-size: 14px;
  font-family: ${fontFamily};
  color: ${colors.form.fontColor};
  padding: 0px 10px;
  outline: none;
  width: ${(props) => (props.theme.responsive ? '100%' : `${props.theme.width}px`)};
  box-sizing: border-box;
  background-color: ${(props) => props.theme.backgroundColor};
  -webkit-appearance: none;

  ::placeholder {
    color: ${colors.form.placeholderFontColor};
    opacity: 1; /* Firefox */
  }

  ::-ms-input-placeholder {
    color: ${colors.form.placeholderFontColor};
  }

  :-ms-input-placeholder {
    color: ${colors.form.placeholderFontColor};
  }

  :focus,
  :active {
    border-color: ${colors.form.activeBorderColor};
  }

  :disabled {
    background-color: ${colors.form.greyDisabled};
    color: ${colors.form.disabledLabelFontColor};
    cursor: not-allowed;
  }
`;

const inputNumberStyle = css`
  input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
    margin: 0;
  }
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
    opacity: 0;
  }
`;

const InputContainer = styled('div')<{ type?: string }>`
  ${(props) => (props.type === 'number' ? inputNumberStyle : '')};
`;

const InputWithLeftMargin = styled(StyledInput)`
  margin-left: 10px;
`;

const ErrorMessage = styled('div')<FormErrorProps>`
  font-size: 12px;
  color: ${colors.dangerRed};
  margin-top: 5px;
  ${({ customStyle }) => customStyle};
`;

const InfoMessage = styled('p')`
  font-weight: ${fontWeight.normal};
  font-size: 0.75rem;
  color: ${colors.gray800};
  margin: 4px 0 0 0;
  text-align: left;
`;

const MessageContainer = styled('div')`
  height: 1.25rem;
  margin-top: 0.25rem;
`;

const FormErrorWithMarginTop = styled(FormError)<FormErrorWithMarginTopProps>`
  margin-top: 5px;
  color: ${colors.dangerRed};
  ${({ customStyle }) => customStyle};
`;

const inputStepArrowStyles = css`
  background-color: #f2f6f7;
  width: 16px;
  cursor: pointer;
  margin-left: -18px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #718087;
  font-size: 10px;
`;

const StepsContainer = styled('div')`
  display: flex;
  flex-direction: column;
`;

const StepUp = styled('div')<{ theme?: Theme }>`
  ${inputStepArrowStyles};
  box-sizing: border-box;
  height: calc(50% - 1px);
  margin-top: 1px;
  min-height: 15px;
  border-top-right-radius: 2px;
  border-left: 1px solid ${(props) => props.theme.borderColor};
  border-bottom: 0.5px solid ${(props) => props.theme.borderColor};
`;

const StepDown = styled('div')<{ theme?: Theme }>`
  ${inputStepArrowStyles};
  box-sizing: border-box;
  height: calc(50% - 1px);
  margin-top: -1px;
  min-height: 16px;
  border-bottom-right-radius: 2px;
  border-left: 1px solid ${(props) => props.theme.borderColor};
  border-top: 0.5px solid ${(props) => props.theme.borderColor};
`;

type FormErrorProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customStyle?: any;
};

type FormErrorWithMarginTopProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customStyle?: any;
};

export interface InputProps {
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errorMessageStyle?: any;
  label?: string;
  invalid?: boolean;
  errorMessage?: string;
  infoMessage?: string;
  errorArea?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?(value: any): void;
  type?: string;
  inputMode?:
    | 'none'
    | 'text'
    | 'numeric'
    | 'search'
    | 'tel'
    | 'url'
    | 'email'
    | 'decimal'
    | undefined;
  min?: number;
  max?: number;
  decimalPrecision?: number; // number of decimal points to display for numeric inputs, default 2
  step?: number;
  value?: string;
  labelPosition?: LabelPosition;
  size?: InputSize;
  className?: string;
  wrapperClassName?: string;
  autoComplete?: string;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.ChangeEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onEnter?: React.ChangeEventHandler<HTMLInputElement>;
  tabIndex?: number;
  autofocus?: boolean;
  autoselect?: boolean;
  labelProps?: LabelProps;
  height?: HeightSize;
  responsive?: boolean;
  dataAnalytics?: string;
  dataPendo?: string;
  dataE2E?: string;
  dataTestid?: string;
  inputRef?: React.MutableRefObject<HTMLInputElement> | ((elem: HTMLInputElement) => void);
  linkUrl?: string;
  linkText?: string;
  tooltipOnDisabled?: boolean; // Shows tooltip containing input value if Input is disabled
  name?: string;
  readOnly?: boolean;
  pasteEdited?: boolean;
}

class Input extends React.Component<InputProps> {
  id = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input: any = null;

  static defaultProps: Partial<InputProps> = {
    labelPosition: 'top',
    size: InputSize.medium,
  };

  constructor(props: InputProps) {
    super(props);

    this.id = props.id ? props.id : this.generateRandomId();
  }

  componentDidMount() {
    if (this.props.autofocus) {
      this.input.focus();
    }
    if (this.props.autoselect) {
      this.input.select();
    }
  }

  focus = () => {
    this.input.focus();
  };

  blur = () => {
    this.input.blur();
  };

  generateRandomId(): string {
    const randomInt = Math.floor(Math.random() * 100 + 1);

    return `input-${randomInt}`;
  }

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { onChange } = this.props;

    if (onChange) {
      onChange(event.target.value);
    }
  };

  renderInputLabel() {
    const { label, labelPosition, labelProps } = this.props;

    if (!label) return null;

    return (
      <Label labelPosition={labelPosition} htmlFor={this.id} {...labelProps}>
        {label}
      </Label>
    );
  }

  renderLink() {
    const { linkUrl, linkText } = this.props;
    if (!linkUrl || !linkText) return null;

    return (
      <GreyLink href={linkUrl} target="_blank">
        {linkText}
      </GreyLink>
    );
  }

  renderErrorMessage() {
    const { errorMessage, errorMessageStyle } = this.props;

    if (!errorMessage) return null;

    return <ErrorMessage customStyle={errorMessageStyle}>{errorMessage}</ErrorMessage>;
  }

  renderNumberInputSteps = () => {
    const handleStepClick = (direction: 'up' | 'down') => () => {
      const { onChange } = this.props;
      if (!this.props.disabled && onChange) {
        onChange(newNumericInputValue(Number(this.input.value), direction));
      }
    };

    const newNumericInputValue = (oldValue: number, direction: 'up' | 'down'): number => {
      const { min, max, decimalPrecision, step } = this.props;
      const stepValue = Number(step) || 1;
      let newValue;
      if (direction === 'up') {
        // don't increment above max
        newValue = max && oldValue + stepValue >= max ? oldValue : oldValue + stepValue;
      } else {
        // don't decrement below min
        newValue = min && oldValue - stepValue <= min ? oldValue : oldValue - stepValue;
      }

      return +newValue.toFixed(decimalPrecision ? decimalPrecision : 2);
    };

    return this.props.type === 'number' ? (
      <StepsContainer style={{ height: this.props.height }}>
        <StepUp onClick={handleStepClick('up')}>
          <i className="fa fa-caret-up" />
        </StepUp>
        <StepDown onClick={handleStepClick('down')}>
          <i className="fa fa-caret-down" />
        </StepDown>
      </StepsContainer>
    ) : (
      ''
    );
  };

  render() {
    const {
      placeholder,
      disabled,
      invalid,
      type,
      inputMode,
      min,
      max,
      step,
      labelPosition,
      size,
      height,
      value,
      autoComplete,
      onFocus,
      onBlur,
      onKeyDown,
      tabIndex,
      className,
      responsive,
      dataAnalytics,
      dataPendo,
      dataE2E,
      dataTestid,
      inputRef,
      tooltipOnDisabled,
      name,
      readOnly,
      pasteEdited,
    } = this.props;

    const theme = {
      responsive,
      height,
      borderColor: (invalid ? colors.dangerRed : colors.form.greyBorder) as string,
      width: size,
      backgroundColor: pasteEdited ? colors.sea25 : null,
    };

    if (disabled) {
      theme.borderColor = colors.form.greyDisabledBorder;
    }

    const inputProps = {
      autoComplete,
      placeholder,
      disabled,
      type,
      inputMode,
      min,
      max,
      step,
      value,
      onFocus,
      onBlur,
      onKeyDown,
      tabIndex,
      className,
      name,
      id: this.id,
      readOnly,
      onChange: this.handleChange,
      ref: (elem: HTMLInputElement) => {
        this.input = elem;
        if (inputRef) {
          if (typeof inputRef === 'function') {
            inputRef(elem);
          } else {
            inputRef.current = this.input;
          }
        }
      },
    };

    let inputLabel;
    let inputElement;
    let inputWithLabel;
    let InputWithLabelContainer;

    // Set label and input element based on label position
    if (labelPosition === 'left') {
      inputLabel = this.renderInputLabel();
      inputElement = (
        <InputWithLeftMargin
          {...inputProps}
          data-analytics={dataAnalytics}
          data-pendo={dataPendo}
          data-e2e={dataE2E}
          data-testid={dataTestid}
        />
      );
      InputWithLabelContainer = FlexContainer;
    } else {
      inputLabel = (
        <LabelWithUrl>
          {this.renderInputLabel()}
          {this.renderLink()}
        </LabelWithUrl>
      );
      inputElement = (
        <StyledInput
          {...inputProps}
          data-analytics={dataAnalytics}
          data-pendo={dataPendo}
          data-e2e={dataE2E}
          data-testid={dataTestid}
        />
      );
      InputWithLabelContainer = React.Fragment;
    }

    if (tooltipOnDisabled && disabled && value) {
      inputElement = (
        <Popup size="mini" inverted position="top center" flowing trigger={inputElement}>
          <div className="block relative text-left">{value}</div>
        </Popup>
      );
    }

    inputWithLabel = (
      <InputWithLabelContainer>
        {inputLabel}
        <ThemeProvider theme={theme}>
          <FlexContainer>
            {inputElement}
            {this.renderNumberInputSteps()}
          </FlexContainer>
        </ThemeProvider>
      </InputWithLabelContainer>
    );

    return (
      <InputContainer className={this.props.wrapperClassName} type={type}>
        {inputWithLabel}
        {this.renderErrorMessage()}
      </InputContainer>
    );
  }
}

interface InputFieldProps {
  hideErrors?: boolean;
  focusedMessage?: string;
}

export const InputField: React.FunctionComponent<
  WrappedFieldProps & InputFieldProps & InputProps
> = (props: WrappedFieldProps & InputFieldProps & InputProps) => {
  const {
    input,
    meta,
    hideErrors,
    errorMessageStyle,
    focusedMessage,
    infoMessage,
    errorArea,
    ...inputProps
  } = props;
  const invalid = meta && meta.touched && meta.error;
  const errors =
    !hideErrors && invalid ? (
      <FormErrorWithMarginTop customStyle={errorMessageStyle}>{meta.error}</FormErrorWithMarginTop>
    ) : null;
  const shouldShowFocusedMessage = focusedMessage && meta.active;
  const message =
    errorArea && !invalid ? (
      <MessageContainer>{infoMessage && <InfoMessage>{infoMessage}</InfoMessage>}</MessageContainer>
    ) : null;

  return (
    <div>
      <Input
        value={input.value}
        onChange={input.onChange}
        onFocus={input.onFocus}
        onBlur={input.onBlur}
        invalid={invalid}
        errorMessageStyle={errorMessageStyle}
        {...inputProps}
        data-pendo={inputProps.dataPendo}
        data-e2e={inputProps.dataE2E}
        data-testid={inputProps.dataTestid}
        name={inputProps.name}
      />
      {message}
      {errors}
      {shouldShowFocusedMessage && (
        <FormFieldFocusedMessage>{focusedMessage}</FormFieldFocusedMessage>
      )}
    </div>
  );
};

interface MaskedInputFieldProps {
  onClick?: () => void; // e.g. clears the field's value on click
}

export const MaskedInputField: React.FunctionComponent<
  WrappedFieldProps & InputFieldProps & InputProps & MaskedInputFieldProps
> = (props: WrappedFieldProps & InputFieldProps & InputProps & MaskedInputFieldProps) => {
  const { input, meta, hideErrors, errorMessageStyle, ...inputProps } = props;
  const invalid = meta && meta.touched && meta.error;
  const errors =
    !hideErrors && invalid ? (
      <FormErrorWithMarginTop customStyle={errorMessageStyle}>{meta.error}</FormErrorWithMarginTop>
    ) : null;

  const [isEditing, setIsEditing] = React.useState(false);

  const maskedTextOnClick = () => {
    if (props.onClick) {
      props.onClick();
    }
    setIsEditing(true);
  };

  const renderMaskedText = () => {
    const tooltipText =
      'This field is masked for security reasons. Click to edit it. Ignore it to keep the previous value.';
    return (
      <StyledMaskedText
        disabled={props.disabled}
        onClick={props.disabled ? undefined : maskedTextOnClick}
        tabIndex={0} // Added for tab accessibility
      >
        {input.value}{' '}
        <Popup
          inverted
          size="mini"
          position="top center"
          trigger={<i className="fa fa-question-circle" />}
        >
          {tooltipText}
        </Popup>
      </StyledMaskedText>
    );
  };

  const renderLabel = () => {
    const { label, labelProps } = props;

    if (!label) return null;

    return <Label {...labelProps}>{label}</Label>;
  };

  return isEditing ? (
    <div>
      <Input
        value={input.value}
        onChange={input.onChange}
        invalid={invalid}
        {...inputProps}
        data-pendo={inputProps.dataPendo}
        data-e2e={inputProps.dataE2E}
        data-testid={inputProps.dataTestid}
      />
      {errors}
    </div>
  ) : (
    <React.Fragment>
      {renderLabel()}
      {renderMaskedText()}
    </React.Fragment>
  );
};

export default Input;
